import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, InjectionToken, inject } from "@angular/core";

import { EMPTY, MonoTypeOperatorFunction } from "rxjs";
import { catchError } from "rxjs/operators";

export const API_URL = new InjectionToken("API_URL");

interface IToastService {
  error(langToken: string | string, duration?: number): void
}

export const ToastServiceToken = new InjectionToken<IToastService>("ToastService");

type ParamsDictionary = { [key: string]: string | string[] };

interface RequestOptions {
  headers?:
  | HttpHeaders
  | {
    [header: string]: string | string[];
  };
  params?: HttpParams | ParamsDictionary;
  reportProgress?: boolean;
  withCredentials?: boolean;
}

@Injectable({ providedIn: "root" })
export class ControllerBase {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastServiceToken);
  apiUrl = inject(API_URL);

  private readonly _handleError = <T>(): MonoTypeOperatorFunction<T> =>
    catchError((error: HttpErrorResponse) => (this.toast.error(error.message), EMPTY));

  readonly handled = {
    get: <T>(url: string, queryString?: HttpParams | ParamsDictionary) => this.get<T>(url, queryString).pipe(this._handleError()),
  };

  get<T>(url: string, queryString?: HttpParams | ParamsDictionary) {
    const options: RequestOptions = queryString ? { params: queryString } : {};
    const req = this.http.get<T>(this.apiUrl + "/" + url, {
      responseType: "json",
      ...options,
    });
    return req;
  }

  post<T>(url: string, body?: any, contentType?: string) {
    const options: RequestOptions = contentType ? { headers: { "Content-Type": contentType } } : {};
    const req = this.http.post<T>(this.apiUrl + "/" + url, body, {
      responseType: "json",
      ...options,
    });
    return req;
  }

  postFiles<T>(url: string, files: File[] | FileList) {
    const formData = new FormData();

    for (const file of Array.from(files)) {
      formData.append("files", file);
    }
    const req = this.http.post<T>(this.apiUrl + "/" + url, formData, {
      responseType: "json",
    });
    return req;
  }

  getText(url: string, queryString?: any) {
    const options: RequestOptions = queryString ? { params: queryString } : {};
    const req = this.http.get(this.apiUrl + "/" + url, {
      responseType: "text",
      ...options,
    });
    return req;
  }

  postText(url: string, body: any, contentType?: string) {
    const req = this.http.post(this.apiUrl + "/" + url, body, {
      responseType: "text",
    });
    return req;
  }

  delete<T>(url: string) {
    const req = this.http.delete<T>(this.apiUrl + "/" + url, {});
    return req;
  }

  toQueryString(args: { [key: string]: any }): string {
    const parts: string[] = [];
    for (const key in args) {
      if (args.hasOwnProperty(key)) {
        const value = args[key];
        if (value !== null && value !== undefined) {
          parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
        }
      }
    }

    return parts.length ? `?${parts.join("&")}` : "";
  }
}
